import React, {useEffect, useState} from 'react'
import useAuth, {extractFromUriFromState} from './useAuth'
import queryString from 'querystring'
import {Loading, SvgPracticeMD_Logo_with_Icon as logo} from '@practicemd/web-components'
import styled from 'styled-components'
import {returnTarget} from '../../auth/accountFlowParams'


import get from "lodash/get"

export interface ContactInfo {
  givenName? : string
  familyName? : string
  email? : string
  isEmailVerified? : boolean
}

export function isContactUpdateRecommended(contactInfo:ContactInfo) {
  if (contactInfo === null || contactInfo === undefined) {
    return false
  }
  try {
    if (!contactInfo.isEmailVerified) {
      return true
    }
    let givenName = (contactInfo?.givenName??"").trim()
    let familyName = (contactInfo?.familyName??"").trim()
    if (givenName.length === 0 || familyName.length === 0) {
      console.log({warning: "not all contact info is populated", contactInfo: contactInfo})
      return true /* ok, let's invite folks to populate names after all */
    }
  } catch (e) {
    return false
  }
  return false
}


const LoginCallback : React.FC<{location: {
    pathname: string;
    search: string;
    state: any;
    hash: string;
    key?: string | undefined;
  }
  /** The email link has signed this browser in: the page takes over with the
   *  arrival steps (EmailLinkArrival), then goes to `returnTo`. */
  onEmailLinkSignedIn: (returnTo: string) => void
}> = ({location, onEmailLinkSignedIn}) => {

  const [message, setMessage] = useState<string>()

  const {authService, authState} = useAuth()

  const [handleCallbackInvoked, setHandleCallbackInvoked] = useState(false)
  useEffect(() => {
    if (handleCallbackInvoked) {
      return
    }
    setHandleCallbackInvoked(true)
    // Email-link completion needs a device in this browser to receive the
    // recovery key; handleCallback provisions it silently when there is
    // none (a new browser is exactly when this login is used). It used to
    // call ensureWasmDeviceProvisioned here, which raises the setup
    // prompter, and since 2026-09-22 that prompter is "Sign in to continue",
    // whose Sign In leads back to the email link: no way in
    // (tests/e2e/legacy-email-login-cold-browser.spec.ts).
    const proceed = async () => {
      await authService.handleCallback(location)
    }
    proceed().catch(e => {
      console.error(e)
      setMessage("Error")
    })
  }, [authService, location, handleCallbackInvoked])

  const [locationAssigned, setLocationAssigned] = useState(false)
  useEffect(() => {
    if (authState.isAuthenticated) {
      // Same origin only, else home: the state is the callback URL's, which
      // anyone can write (returnTarget).
      let fromUri = returnTarget({fromUri: determineFromUri(location)})
      console.log('[LoginCallback] Extracted fromUri from state:', fromUri);

      const contactInfo : ContactInfo = {
        givenName: (get(authState, 'registeredUser.given_name')??"").trim(),
        familyName: (get(authState, 'registeredUser.family_name')??"").trim(),
        email: (get(authState, 'registeredUser.email')??"").trim(),
        isEmailVerified: get(authState, 'registeredUser.email_verified', false) || false,
      }

      if (fromUri.indexOf('confirm_email') > 0) {
        console.log('[LoginCallback] Redirecting to:', fromUri);
        document.location.assign(fromUri) /* complete email verification */
      } else {
        // Email authentication implies the account holds a legacy recovery
        // device: the arrival steps protect this browser and, when the
        // account has no kit, make one, before the return. They are hosted
        // here, not on /authorize/?flow=migration as they used to be: that
        // page shows its signed-in content to anyone who arrives signed in,
        // so since 2026-08-28 they never showed.
        onEmailLinkSignedIn(fromUri ?? "/")
      }
      setLocationAssigned(true)
    } else {
    }

  }, [authState, authState.isAuthenticated, location, locationAssigned])


    return (
      <Container>
        <img src={logo} alt="logo" width="300px"/>
        {
          message
            ?
              <ErrorMessage msg={message}/>
            :
            <LoadingMessage/>
        }
      </Container>
    )
}

const LoadingMessage = () => <div id="message"><div id="loading"><Loading/></div>Please Wait</div>
const ErrorMessage = ({msg}:{msg?:string}) => <div id="message"><p>{msg ? msg : 'There appears to be a problem'}</p></div>

export default LoginCallback

const Container = styled.div`
  min-height: 100%;
  background-color: ${'#fff'};
  
  font-family: "Open Sans";

  display: flex;
  flex-direction: column;
  
  align-items: center;
  justify-content: center;
  
  #message {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    min-width: 100px;
    max-width: 800px;
  }
  
  #loading {
  }
`
function determineFromUri(location: {
                            pathname: string;
                            search: string;
                            state: any;
                            hash: string;
                            key?: string | undefined;
                          }
) {
  if (location.search.startsWith("?")) {
    location.search = location.search.slice(1)
  }
  const values = queryString.parse(location.search)
  try {
    const state = get(values, 'state', null)
    if (typeof (state) !== 'string') {
      throw new Error(`type of ${JSON.stringify(state)} is unexpected`)
    }
    return extractFromUriFromState(state)
  } catch (e) {
    return '/'
  }
}
